import { Component, OnInit } from '@angular/core';
import { CustomerReplyPortalService } from '../core/services/customer-reply-portal.service';
import { CustomerReplyPortalTicketModel } from '../core/models/customer-reply-portal-ticket-model.model';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';
import { AttachmentsModalComponent } from './attachments-modal/attachments-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { CustomerReplyPortalReplyModelBody } from '../core/models/customer-reply-portal-reply-model-body.model';
import { ReplyTemplateManagerService } from '@sb-tools/typescript/services/reply-template-manager.service';
import Quill from 'quill';
import { Range } from 'quill/core/selection';
import { CustomerReplyPortalReplyModel } from '../core/models/customer-reply-portal-reply-model.model';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { ActivatedRoute } from '@angular/router';
import { SignalRService } from '../core/services/signalR.service';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { environment } from '@environment';
import { CustomerService } from '../core/services/customer.service';
import { PublicCustomer } from '../core/models/public-customer.model';
import { HelperService } from '@sb-tools/typescript/services/helper.service';
import { SmilesService } from '@sb-tools/typescript/services/smiles.service';
import { Error } from '@sb-tools/typescript/enums/error.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { LocalizationLanguage } from '@sb-tools/typescript/enums/localization-language.enum';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { TicketReplyComponent } from './ticket-reply/ticket-reply.component';
import { NgClass } from '@angular/common';
import { QuillEditorComponent } from 'ngx-quill';
import { FormsModule } from '@angular/forms';
import { QuillEmojiPickerComponent } from '@sb-tools/typescript/components/quill-emoji-picker/quill-emoji-picker.component';
import { FilePreviewComponent } from '@sb-document-viewer/components/file-preview/file-preview.component';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { IsInvalidMessagePipe } from '@sb-tools/typescript/pipes/ticket/is-invalid-message.pipe';

@Component({
    selector: 'app-ticket',
    templateUrl: './ticket.component.html',
    styleUrls: ['./ticket.component.scss'],
    imports: [
        TicketReplyComponent,
        NgClass,
        QuillEditorComponent,
        FormsModule,
        QuillEmojiPickerComponent,
        FilePreviewComponent,
        TooltipDirective,
        InlineSVGComponent,
        TranslateModule,
        IsInvalidMessagePipe,
    ],
})
export class TicketComponent implements OnInit {
    attachments = [];
    customer: PublicCustomer;
    firstMessage: CustomerReplyPortalReplyModel;
    isLinkExpired: boolean;
    isSendingReply: boolean;
    newReply: CustomerReplyPortalReplyModelBody;
    newReplyEditor: HTMLElement;
    newReplyEditorLastHeight;
    quillEditor: Quill;
    quillRange: Range;
    ticket: CustomerReplyPortalTicketModel;

    private unsubscriber = new Subject<void>();

    constructor(
        private customerReplyPortalService: CustomerReplyPortalService,
        public quillHelperService: QuillHelperService,
        private bsModalService: BsModalService,
        public replyTemplateManagerService: ReplyTemplateManagerService,
        private commonDataService: CommonDataService,
        private activatedRoute: ActivatedRoute,
        private signalRService: SignalRService,
        private customerService: CustomerService,
        private smilesService: SmilesService,
        private translateService: TranslateService,
        private title: Title,
    ) {}

    ngOnInit(): void {
        this.commonDataService.xToken = this.activatedRoute.snapshot.queryParamMap.get('token');
        this.commonDataService.community = this.activatedRoute.snapshot.paramMap.get('community');
        this.newReply = new CustomerReplyPortalReplyModelBody();

        this.customerService
            .getCustomer()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe({
                next: (customer) => {
                    this.customer = customer;
                    this.setupLanguage(this.customer.language);
                    this.title.setTitle(
                        this.translateService.instant('Customer reply portal') + ' - ' + this.customer.displayName,
                    );
                },
            });

        this.customerReplyPortalService
            .getTicket()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe({
                next: (ticket) => {
                    environment.signalrServerUrl = ticket.heartbeatUrl;
                    this.signalRService.reconnect();
                    this.setupSignalRListeners();

                    this.ticket = ticket;

                    this.firstMessage = {
                        attachments: this.ticket.attachments,
                        author: this.ticket.firstMessageCreator,
                        creationTime: this.ticket.creationTime,
                        id: 0,
                        isByTeam: false,
                        message: this.ticket.message,
                    };
                },
                error: (error: HttpErrorResponse) => {
                    this.onLinkExpiredError(error);
                },
            });
    }

    addReply(): void {
        this.isSendingReply = true;
        this.processNewReplyHtml();
        this.customerReplyPortalService
            .addReply(this.newReply)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isSendingReply = false;
                }),
            )
            .subscribe({
                next: (addedReply) => {
                    if (
                        !this.ticket.messageThread.find((reply) => {
                            return reply.id === addedReply.id;
                        })
                    ) {
                        this.ticket.messageThread.push(addedReply);
                    }

                    this.newReply = new CustomerReplyPortalReplyModelBody();
                },
                error: (error: HttpErrorResponse) => {
                    this.onLinkExpiredError(error);
                },
            });
    }

    onEditorCreated(quill: Quill | any): void {
        this.quillEditor = quill;

        quill.getModule('toolbar').addHandler('image', () => {
            this.quillHelperService.selectLocalImage(quill);
        });

        setTimeout(() => {
            this.quillEditor.setSelection(0);
            this.quillEditor.focus();
        }, 100);
    }

    onLinkExpiredError(error: HttpErrorResponse): void {
        if (error.error?.errorId === Error.linkIsExpired) {
            this.isLinkExpired = true;
        }
    }

    onReplyChanged(): void {
        this.newReply.message.text = HelperService.strip(
            this.smilesService.replaceImagesToCodes(this.newReply.message.html),
            false,
        );

        this.controlVisibilityOfNewReplyField();
    }

    openAttachmentsModal(): void {
        const initialState = {
            item: null,
            newAttachments: this.newReply.attachments,
            existedAttachmentsLength: this.newReply.attachments.length,
            isInternalComment: true,
        };

        this.bsModalService.show(AttachmentsModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    updateQuillSelection(quill: Quill): void {
        this.quillRange = this.replyTemplateManagerService.updateRange(quill);
    }

    private controlVisibilityOfNewReplyField() {
        if (!this.newReplyEditor) {
            this.initializeNewReplyEditor();
        }

        const currentHeight = $(this.newReplyEditor).height();

        if (currentHeight > this.newReplyEditorLastHeight) {
            const container = $('body');

            setTimeout(() => {
                container.animate({ scrollTop: container.prop('scrollHeight') }, 1000);
            }, 100);
        }

        this.newReplyEditorLastHeight = currentHeight;
    }

    private initializeNewReplyEditor(): void {
        try {
            this.newReplyEditor = $('#new-reply .ql-editor')[0];
            this.newReplyEditorLastHeight = $(this.newReplyEditor).height();
        } catch {}
    }

    private processNewReplyHtml(): void {
        this.newReply.message.html = this.quillHelperService.removeResizeCursorFromImages(this.newReply.message.html);
    }

    private setupLanguage(language: LocalizationLanguage): void {
        switch (language) {
            case LocalizationLanguage.norwegian:
                this.commonDataService.language = 'nb';
                this.translateService.use('nb');
                break;
            case LocalizationLanguage.swedish:
                this.commonDataService.language = 'sv_SE';
                this.translateService.use('sv_SE');
                break;
            case LocalizationLanguage.danish:
                this.commonDataService.language = 'da_DK';
                this.translateService.use('da_DK');
                break;
            default:
                this.commonDataService.language = 'en';
                this.translateService.use(undefined);
        }
    }

    private setupSignalRListeners(): void {
        this.signalRService
            .onReplyAdded()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((newReply: CustomerReplyPortalReplyModel) => {
                if (
                    !this.ticket.messageThread.find((reply) => {
                        return reply.id === newReply.id;
                    })
                ) {
                    if (newReply.isByTeam) {
                        newReply.isNewMessageFromAgent = true;
                    }

                    this.ticket.messageThread.push(newReply);
                }
            });
    }
}
